<script lang="ts">
  import { copyText } from '#lib/platform/clipboard.js';
  import { formatTime } from '#lib/ui/date-time.js';
  import { i18n } from '#lib/i18n.js';
  import Button from '#lib/ui/primitives/Button.svelte';
  import Select from '#lib/ui/primitives/Select.svelte';
  import SettingsRow from '#lib/ui/primitives/SettingsRow.svelte';
  import Switch from '#lib/ui/primitives/Switch.svelte';
  import {
    clearDebugLogs,
    debugLog,
    exportDebugLogs,
    setDebugLogging,
    type DebugLogCategory,
    type DebugLogLevel,
  } from '#lib/observability/debug-log.svelte.js';
  import { exportDiagnosticsBundle } from '#lib/observability/export-diagnostics.js';
  import '#lib/ui/primitives/settings-row.css';
  import { scrollHint } from '#lib/ui/scroll-hint.js';

  const categories: DebugLogCategory[] = [
    'sync',
    'network',
    'notification',
    'message',
    'media',
    'call',
    'ui',
    'timeline',
    'error',
    'general',
  ];
  const levels: (DebugLogLevel | 'all')[] = ['all', 'debug', 'info', 'warn', 'error'];
  let level = $state<DebugLogLevel | 'all'>('all');
  let category = $state<DebugLogCategory | 'all'>('all');
  let filtered = $derived(
    debugLog.entries.filter(
      (entry) =>
        (level === 'all' || entry.level === level) &&
        (category === 'all' || entry.category === category)
    )
  );

  async function download(entries = filtered): Promise<void> {
    await exportDiagnosticsBundle(entries);
  }

  async function copy(entries = filtered): Promise<void> {
    await copyText(exportDebugLogs(entries));
  }
</script>

<div class="logs">
  <ul class="settings">
    <SettingsRow
      id="debug-logs-status"
      title={i18n.t('settings.developerLogsStatus')}
      description={i18n.t('settings.developerLogsDescription')}
    >
      <Switch
        label={i18n.t('settings.developerLogsEnable')}
        checked={debugLog.enabled}
        onCheckedChange={setDebugLogging}
      />
    </SettingsRow>
  </ul>

  <div class="settings-form logs-body">
    <div class="filters">
      <Select
        aria-label={i18n.t('settings.developerLogsLevel')}
        value={level}
        items={levels.map((value) => ({ value, label: value }))}
        onValueChange={(value) => (level = value as DebugLogLevel | 'all')}
      />
      <Select
        aria-label={i18n.t('settings.developerLogsCategory')}
        value={category}
        items={[
          { value: 'all', label: 'all' },
          ...categories.map((value) => ({ value, label: value })),
        ]}
        onValueChange={(value) => (category = value as DebugLogCategory | 'all')}
      />
    </div>

    <div class="actions">
      <Button variant="ghost" size="small" onclick={clearDebugLogs}>{i18n.t('common.clear')}</Button
      >
      <Button variant="secondary" size="small" onclick={() => void copy()}
        >{i18n.t('settings.developerLogsCopy')}</Button
      >
      <Button variant="secondary" size="small" onclick={() => void download()}
        >{i18n.t('settings.developerLogsExport')}</Button
      >
    </div>

    {#if filtered.length === 0}
      <p class="empty">{i18n.t('settings.developerLogsEmpty')}</p>
    {:else}
      <div class="entries" aria-live="polite" {@attach scrollHint}>
        {#each filtered.slice(-200).reverse() as entry (entry.id)}
          <details class={`entry level-${entry.level}`}>
            <summary>
              <span>{entry.level.toUpperCase()}</span>
              <span>{entry.category}</span>
              <time datetime={new Date(entry.timestamp).toISOString()}
                >{formatTime(entry.timestamp)}</time
              >
              <span>{entry.namespace}</span>
            </summary>
            <p>{entry.message}</p>
            {#if entry.data}<pre>{JSON.stringify(entry.data, null, 2)}</pre>{/if}
          </details>
        {/each}
      </div>
    {/if}
  </div>
</div>

<style>
  .logs {
    display: grid;
  }

  .logs-body,
  .entries {
    display: grid;
    gap: var(--space-300);
  }

  .filters,
  .actions {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-300);
  }

  p {
    margin: 0;
  }

  p,
  .empty {
    color: var(--surface-var-on-container);
  }

  .filters > :global(*) {
    min-width: 9rem;
  }

  .actions {
    justify-content: flex-end;
  }

  .settings {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .entries {
    max-height: 32rem;
    overflow: auto;
  }

  .entry {
    background: var(--bg-container);
    border-radius: var(--radius);
    color: var(--bg-on-container);
    padding: var(--space-200) var(--space-300);
  }

  summary {
    cursor: pointer;
    display: grid;
    font-family: var(--font-family-mono);
    font-size: var(--font-size-small);
    font-variant-ligatures: none;
    gap: var(--space-200);
    grid-template-columns: auto auto auto 1fr;
  }

  summary span:first-child {
    font-weight: var(--font-weight-bold);
  }

  .level-error summary span:first-child {
    color: var(--crit-main);
  }

  .level-warn summary span:first-child {
    color: var(--warn-main);
  }

  .entry p,
  pre {
    font-family: var(--font-family-mono);
    font-size: var(--font-size-small);
    font-variant-ligatures: none;
    margin: var(--space-200) 0 0;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
  }

  pre {
    max-height: 12rem;
    overflow: auto;
  }
</style>
